import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NoddiBadge, NoddiButton, NoddiSheet, useIsMobile } from 'noddi-ui-web';
import { getDimensionsLoadSpeedString, getTireBrandName } from 'noddi-util';
import { TireEuGradeBadge } from 'noddi-web';
import type { ReactNode } from 'react';
import type { TireOptionQuantityRows } from './tireOptionQuantities';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: TireOptionQuantityRows;
  classification?: string;
  /** The option's total with the quantities on its card. */
  total: string;
  onChoose: () => void;
};

/** "More about the tire": the product, what its EU label grades mean, and its sizes. */
export const TireOptionInfoSheet = ({ open, onOpenChange, rows, classification, total, onChoose }: Props) => {
  const isMobile = useIsMobile();
  const [{ tire: headlineTire }] = rows;
  const tire = headlineTire.tireInventory.tire;
  const imageUrl = headlineTire.tireInventory.imageTire?.url ?? headlineTire.tireInventory.imageUrl;

  return (
    <NoddiSheet
      open={open}
      onOpenChange={onOpenChange}
      side={isMobile ? 'bottom' : 'right'}
      title={tire.model ?? getTireBrandName(tire)}
      footer={
        <NoddiButton className='w-full' onClick={onChoose}>
          <Trans>Choose this tire</Trans>
        </NoddiButton>
      }
    >
      <div className='flex flex-col gap-5'>
        {imageUrl && <img src={imageUrl} alt='' className='h-40 w-auto self-center object-contain' />}
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium text-muted-foreground'>{getTireBrandName(tire)}</span>
          {classification && <NoddiBadge variant='outline'>{classification}</NoddiBadge>}
        </div>

        {(tire.wetGripClass || tire.rollingResistanceClass || tire.soundDb) && (
          <div className='flex flex-col gap-3'>
            <span className='text-sm font-semibold text-primary'>
              <Trans>EU tire label</Trans>
            </span>
            {tire.wetGripClass && (
              <EuLabelRow
                value={<TireEuGradeBadge grade={tire.wetGripClass} className='size-8 rounded-md text-sm' />}
                label={t`Wet grip`}
                description={t`Braking distance on wet roads. A is best.`}
              />
            )}
            {tire.rollingResistanceClass && (
              <EuLabelRow
                value={<TireEuGradeBadge grade={tire.rollingResistanceClass} className='size-8 rounded-md text-sm' />}
                label={t`Fuel economy`}
                description={t`Rolling resistance. Affects consumption and range.`}
              />
            )}
            {tire.soundDb ? (
              <EuLabelRow
                value={
                  <span className='flex size-8 items-center justify-center rounded-md bg-muted text-xs font-bold'>
                    {tire.soundDb}
                  </span>
                }
                label={t`Noise level, dB`}
                description={t`Noise outside the car. Lower is quieter.`}
              />
            ) : null}
          </div>
        )}

        <div className='flex flex-col gap-1 rounded-lg bg-muted/60 p-4 text-sm'>
          <span className='font-semibold text-primary'>
            <Trans>Dimensions</Trans>
          </span>
          {rows.map((row) => (
            <span key={row.tire.id}>
              {rows.length > 1
                ? `${row.label}: ${getDimensionsLoadSpeedString(row.tire.tireInventory.tire)}`
                : getDimensionsLoadSpeedString(row.tire.tireInventory.tire)}
            </span>
          ))}
        </div>

        <div className='flex items-baseline justify-between gap-4'>
          <span className='text-sm text-muted-foreground'>
            <Trans>Total for your choice</Trans>
          </span>
          <span className='text-xl font-bold'>{total}</span>
        </div>
      </div>
    </NoddiSheet>
  );
};

const EuLabelRow = ({ value, label, description }: { value: ReactNode; label: string; description: string }) => (
  <div className='flex items-center gap-3'>
    {value}
    <div className='flex flex-col'>
      <span className='font-semibold'>{label}</span>
      <span className='text-sm text-muted-foreground'>{description}</span>
    </div>
  </div>
);
